import {
  Box,
  InputAdornment,
  OutlinedTextFieldProps,
  Stack,
  TextField,
  Typography,
  keyframes,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useMeasure } from 'react-use';

// TODO: Find a way to use width: 100%, it's not working with absolute positioned adornment
const TYPING_KEYFRAMES = keyframes`
   0% {
    width: 0;
  }
  100% {
    width: 150ch;
  }
`;

const FADE_KEYFRAMES = keyframes`
  0%, 80% { opacity: 1; }
  90%, 100% { opacity: 0; }
`;

interface TextFieldWithAnimatedPlaceholderProps extends OutlinedTextFieldProps {
  placeholders: readonly string[];
  startAdornment?: React.ReactNode;
  onClickPlaceholder?: () => void;
  defaultPlaceholder: string;
}

export const TextFieldWithAnimatedPlaceholder = ({
  placeholders,
  startAdornment,
  defaultPlaceholder,
  onClickPlaceholder, // This is needed, because onClick events don't work on MUI's input adornment, for ex. for Autocomplete component's popper
  ...props
}: TextFieldWithAnimatedPlaceholderProps) => {
  const [currentIndex, setCurrentIndex] = useState<number>();

  const [inputRef, { width: inputWidth }] = useMeasure();

  const [isFocused, setIsFocused] = useState(false);

  const isFirstRender = currentIndex === undefined;

  const shouldShowPlaceholder =
    !props.value && !props.inputProps?.value && !isFocused;

  useEffect(() => {
    let interval;

    if (!shouldShowPlaceholder) {
      clearInterval(interval);
      setCurrentIndex(undefined);
      return;
    }

    interval = setInterval(
      () => {
        setCurrentIndex((prevIndex) => {
          if (prevIndex === undefined) {
            return 0;
          }
          return (prevIndex + 1) % placeholders.length;
        });
      },
      isFirstRender ? 3000 : 6000,
    );

    return () => clearInterval(interval);
  }, [isFirstRender, placeholders.length, shouldShowPlaceholder]);

  const currentPlaceholder = !isFirstRender
    ? placeholders[currentIndex] ?? defaultPlaceholder
    : defaultPlaceholder;

  return (
    <TextField
      {...props}
      placeholder={undefined}
      inputRef={inputRef}
      onFocus={(e) => {
        setIsFocused(true);
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        props.onBlur?.(e);
      }}
      InputProps={{
        ...props.InputProps,
        startAdornment: (
          <InputAdornment position="start">
            <Stack direction="row" alignItems="center" columnGap={1}>
              {startAdornment}
              {shouldShowPlaceholder && (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                  }}
                  onClick={onClickPlaceholder}
                >
                  <Typography
                    key={currentPlaceholder}
                    variant="subtitle1"
                    sx={{
                      color: 'text.disabled',
                      display: 'inline-block',
                      overflow: 'hidden',
                      animation: isFirstRender
                        ? `${FADE_KEYFRAMES} 3s forwards`
                        : `${TYPING_KEYFRAMES} 4s steps(${currentPlaceholder.length}), ${FADE_KEYFRAMES} 6s forwards`,
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      left: '14px',
                      whiteSpace: 'nowrap',
                      maxWidth: `${inputWidth}px`,
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {currentPlaceholder}
                  </Typography>
                </Box>
              )}
            </Stack>
          </InputAdornment>
        ),
      }}
    />
  );
};
